/**
 * Llamadas a la API de adjuntos y su validación previa (solo para avisar rápido; la API decide).
 */
import {
  CODIGOS_ERROR_ADJUNTOS,
  LISTA_MIME_PERMITIDOS,
  MAXIMO_ADJUNTOS_POR_TAREA,
  MIME_PERMITIDOS,
  TAMANO_MAXIMO_ADJUNTO,
  adjuntoSchema,
  esMimePermitido,
  listaAdjuntosSchema,
  type MimePermitido,
} from '@cdtec-backlog/contrato';
import { ErrorDeApi, mensajeDeError, solicitar, solicitarTexto, subirArchivo } from '../../lib/api';
import { formatearTamano } from '../../lib/formato';
import { MAX_BYTES_TEXTO } from './previsualizacion';

export const listarAdjuntos = (tareaId: number) =>
  solicitar(`/tareas/${tareaId}/adjuntos`, { esquema: listaAdjuntosSchema });

/** `alProgresar` recibe la fracción enviada (0 a 1) para la barra de progreso. */
export const subirAdjunto = (
  tareaId: number,
  archivo: File,
  tipo: string,
  alProgresar?: (fraccion: number) => void,
) =>
  subirArchivo(`/tareas/${tareaId}/adjuntos?nombre=${encodeURIComponent(archivo.name)}`, archivo, {
    tipo,
    esquema: adjuntoSchema,
    alProgresar,
  });

export const eliminarAdjunto = (id: number) => solicitar(`/adjuntos/${id}`, { metodo: 'DELETE' });

/** Contenido de un adjunto de texto para el visor (hasta `MAX_BYTES_TEXTO`). */
export const leerTextoAdjunto = (id: number) =>
  solicitarTexto(`/adjuntos/${id}`, { maxBytes: MAX_BYTES_TEXTO });

/** Dirección de descarga (`attachment`) o, con `enLinea`, para ver la imagen en la página. */
export const urlAdjunto = (id: number, enLinea = false) =>
  `/api/adjuntos/${id}${enLinea ? '?inline=1' : ''}`;

/** Valor del atributo `accept` del selector de archivos. */
export const ACEPTAR = [
  ...LISTA_MIME_PERMITIDOS,
  ...LISTA_MIME_PERMITIDOS.flatMap((mime) => MIME_PERMITIDOS[mime].extensiones.map((e) => `.${e}`)),
].join(',');

/**
 * Tipo del archivo: el que declara el navegador y, si viene vacío (pasa con algunos .csv o .docx),
 * el que corresponde a su extensión. `null` si no está permitido.
 */
export function tipoDeArchivo(archivo: { name: string; type: string }): MimePermitido | null {
  if (esMimePermitido(archivo.type)) return archivo.type;
  const extension = archivo.name.split('.').pop()?.toLowerCase() ?? '';
  return (
    LISTA_MIME_PERMITIDOS.find((mime) =>
      (MIME_PERMITIDOS[mime].extensiones as readonly string[]).includes(extension),
    ) ?? null
  );
}

/** Mensaje de por qué no se puede subir el archivo, o `null` si está bien. `cupo` = cuántos caben aún. */
export function validarArchivo(
  archivo: { name: string; type: string; size: number },
  cupo: number,
): string | null {
  if (cupo <= 0) {
    return `«${archivo.name}»: la tarea ya tiene el máximo de ${MAXIMO_ADJUNTOS_POR_TAREA} archivos.`;
  }
  if (tipoDeArchivo(archivo) === null) {
    return `«${archivo.name}»: ese tipo de archivo no está permitido.`;
  }
  if (archivo.size === 0) return `«${archivo.name}»: el archivo está vacío.`;
  if (archivo.size > TAMANO_MAXIMO_ADJUNTO) {
    return `«${archivo.name}»: pesa más de ${formatearTamano(TAMANO_MAXIMO_ADJUNTO)}.`;
  }
  return null;
}

/** Mensaje en español según el código de error de la API. */
export function mensajeDeErrorAdjunto(error: unknown): string {
  if (error instanceof ErrorDeApi) {
    switch (error.codigo) {
      case CODIGOS_ERROR_ADJUNTOS.demasiadoGrande:
        return `El archivo pesa más de ${formatearTamano(TAMANO_MAXIMO_ADJUNTO)}.`;
      case CODIGOS_ERROR_ADJUNTOS.tipoNoPermitido:
        return 'Ese tipo de archivo no está permitido. Puedes subir imágenes, PDF, texto, CSV, Word, Excel y PowerPoint.';
      case CODIGOS_ERROR_ADJUNTOS.contenidoInvalido:
        return 'El contenido del archivo no corresponde a su tipo. Revisa que no esté dañado.';
      case CODIGOS_ERROR_ADJUNTOS.limite:
        return `La tarea ya tiene el máximo de ${MAXIMO_ADJUNTOS_POR_TAREA} archivos. Elimina alguno para subir otro.`;
      case CODIGOS_ERROR_ADJUNTOS.vacio:
        return 'El archivo está vacío.';
      case 'adjunto_no_disponible':
        return 'El archivo ya no está disponible en el servidor. Avísale a administración.';
    }
  }
  return mensajeDeError(error);
}
